/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import './PayoutForm.css';

// New order structure
export interface PayoutOrder {
  id: string;
  number: string;
  amount: number; 
}

interface PayoutFormProps {
  orderId: string;
  orderNumber: string;
  payoutAmount: number;
  isAdmin: boolean;
  onSubmit?: (payoutData: PayoutData) => void;
  checkedOrders?: PayoutOrder[];
  initialData?: {
    date: string;
    amount: number;
    method: string;
    confirmationNumber: string;
    notes?: string;
  };
  collectionName?: string;
}

export interface PayoutData {
  orderId: string;
  orderNumber: string;
  date: string;
  amount: number;
  method: string;
  confirmationNumber: string;
  notes: string;
  orders?: PayoutOrder[];
  collectionName?: string;
}

const PayoutForm: React.FC<PayoutFormProps> = ({
  orderId,
  orderNumber,
  payoutAmount,
  isAdmin,
  onSubmit,
  checkedOrders = [],
  initialData,
  collectionName
}) => {
  console.log('📝 PayoutForm received checkedOrders:', checkedOrders);
  console.log('📝 First checkedOrder:', checkedOrders[0]);
  console.log('📝 isAdmin (from custom claims):', isAdmin);
  
  // Get today's date in MM-DD-YYYY format
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const year = today.getFullYear();
  const formattedDate = `${month}-${day}-${year}`;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (onSubmit) {
      const formData = new FormData(e.currentTarget);
      
      // Validate that all orders have amounts
      const ordersWithAmounts = checkedOrders.filter(order => 
        order.amount !== undefined && order.amount !== null
      );
      
      if (ordersWithAmounts.length !== checkedOrders.length) {
        console.warn('⚠️ Some orders are missing amount data');
      }
      
      const payoutData: PayoutData = {
        orderId,
        orderNumber,
        date: formData.get('payout-date') as string,
        amount: parseFloat(formData.get('payout-amount') as string),
        method: formData.get('payout-method') as string,
        confirmationNumber: formData.get('payout-confirmation') as string,
        notes: formData.get('payout-notes') as string,
        orders: ordersWithAmounts,
        collectionName
      };
      
      console.log('📤 Submitting payout with order amounts:', payoutData.orders);
      onSubmit(payoutData);
    }
  };

  return (
    <div className="expanded-section order-payout-expanded">
      <h4 className="expanded-title">
        Payout Information for:<br/><br/>
        {collectionName && <strong>{collectionName}</strong>}
      </h4>
      <div className="payout-info-section" style={{
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        {checkedOrders.length > 0 && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#e7f3ff',
            borderRadius: '4px',
            border: '1px solid #b3d9ff'
          }}>
            <h5 style={{ marginTop: 0, marginBottom: '10px', color: '#004085' }}>
              Selected Orders ({checkedOrders.length})
            </h5>
            <div style={{
              backgroundColor: 'white',
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid #d1e7ff'
            }}>
              {checkedOrders.map((order) => (
                <div key={order.id} style={{
                  textAlign: 'left',
                  padding: '8px',
                  marginBottom: '12px',
                  border: '1px solid #dddddd',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '3px',
                  fontSize: '13px',
                  color: '#495057'
                }}>
                  <div style={{ fontWeight: 'bold' }}>#{order.number}</div>
                  <div style={{ fontWeight: 'bold' }}>ID: {order.id}</div>
                  {order.amount !== undefined && (
                    <div style={{ color: '#28a745', fontWeight: 'bold' }}>
                      Amount: ${order.amount.toFixed(2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <form className="payout-form" onSubmit={handleSubmit}>
          <div className="payout-form__group">
            <label htmlFor={`payout-date-${orderId}`} className="payout-form__label">
              Payout Date:
            </label>
            <input
              type="text"
              id={`payout-date-${orderId}`}
              name="payout-date"
              defaultValue={initialData?.date || formattedDate}
              placeholder="MM-DD-YYYY"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div className="payout-form__group">
            <label htmlFor={`payout-amount-${orderId}`} className="payout-form__label">
              Payout Amount:
            </label>
            <input
              type="number"
              id={`payout-amount-${orderId}`}
              name="payout-amount"
              defaultValue={(() => {
                const amount = initialData?.amount ?? payoutAmount ?? 0;
                return Number(amount).toFixed(2);
              })()}
              step="0.01"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          {isAdmin && (
            <div className="payout-form__group">
              <label htmlFor={`payout-method-${orderId}`} className="payout-form__label">
                Payment Method:
              </label>
              <select
                id={`payout-method-${orderId}`}
                name="payout-method"
                defaultValue={initialData?.method || "check"}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="check">Check</option>
                <option value="venmo">Venmo</option>
                <option value="paypal">PayPal</option>
                <option value="direct-deposit">Direct Deposit</option>
                <option value="cash-app">Cash App</option>
              </select>
            </div>
          )}

          <div className="payout-form__group">
            <label htmlFor={`payout-confirmation-${orderId}`} className="payout-form__label">
              Confirmation Number:
            </label>
            <input
              type="text"
              id={`payout-confirmation-${orderId}`}
              name="payout-confirmation"
              defaultValue={initialData?.confirmationNumber || ""}
              placeholder="Enter confirmation number..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div className="payout-form__group">
            <label htmlFor={`payout-notes-${orderId}`} className="payout-form__label">
              Notes:
            </label>
            <textarea
              id={`payout-notes-${orderId}`}
              name="payout-notes"
              rows={4}
              defaultValue={initialData?.notes || ""}
              placeholder="Add any notes about this payout..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn--small"
            style={{
              alignSelf: 'flex-end',
              marginTop: '10px'
            }}
          >
            Submit Payout
          </button>
        </form>
      </div>
    </div>
  );
};

export default PayoutForm;